// ============================================================================
// NPC State - Client-side NPC entity management
// ============================================================================

import {
  DietType,
  NPC_DEFINITIONS,
  type NpcSnapshot,
  type NpcTypeValue,
  TILE_SIZE,
  toNpcType,
} from '@dreamgame/shared';
import { computed, signal } from '@preact/signals';

// ============================================================================
// Types
// ============================================================================

// Interpolation data for smooth movement
export interface NpcInterpolationData {
  readonly prevX: number;
  readonly prevY: number;
  readonly targetX: number;
  readonly targetY: number;
  readonly startTime: number;
}

// Client-side NPC data extends snapshot with rendering state
export interface NpcData {
  readonly networkId: number;
  readonly npcType: NpcTypeValue;
  readonly x: number;
  readonly y: number;
  readonly vx: number;
  readonly vy: number;
  readonly health: number;
  readonly maxHealth: number;
  readonly aiState: number;
  readonly packId: number;
  readonly isLeader: boolean;
  readonly hunger: number;
  readonly tiredness: number;
  readonly fear: number;
  readonly targetEntityId: number;
  // Client-only rendering state
  readonly interpolation: NpcInterpolationData | null;
  readonly animationFrame: number;
  readonly lastFrameTime: number;
}

// ============================================================================
// State
// ============================================================================

// Using mutable Map with version counter (like entities.ts) for performance
const npcsMap = new Map<number, NpcData>();
export const npcsVersion = signal(0);

// Accessor for the NPCs map (read-only access for external code)
export const npcs = {
  get value(): Map<number, NpcData> {
    return npcsMap;
  },
};

// Increment version to notify dependents of changes
const notifyNpcsChanged = (): void => {
  npcsVersion.value = npcsVersion.value + 1;
};

// ============================================================================
// Derived State
// ============================================================================

// PERFORMANCE: Cache for allNpcs to avoid Array.from() every frame
let _allNpcsCache: NpcData[] = [];
let _allNpcsCacheVersion = -1;

export const allNpcs = computed(() => {
  const version = npcsVersion.value;
  if (version !== _allNpcsCacheVersion) {
    _allNpcsCacheVersion = version;
    _allNpcsCache = Array.from(npcsMap.values());
  }
  return _allNpcsCache;
});

export const npcsByType = computed(() => {
  const byType = new Map<NpcTypeValue, NpcData[]>();
  for (const npc of npcs.value.values()) {
    const existing = byType.get(npc.npcType) ?? [];
    existing.push(npc);
    byType.set(npc.npcType, existing);
  }
  return byType;
});

export const predatorNpcs = computed(() =>
  allNpcs.value.filter((a) => {
    const def = NPC_DEFINITIONS.get(a.npcType);
    return def && def.diet === DietType.CARNIVORE;
  })
);

// ============================================================================
// Constants
// ============================================================================

const SNAPSHOT_INTERVAL_MS = 110;

// ============================================================================
// Actions
// ============================================================================

export const addNpc = ({ npc }: { npc: NpcSnapshot }): void => {
  const npcData: NpcData = {
    networkId: npc.networkId,
    npcType: toNpcType(npc.npcType),
    x: npc.x,
    y: npc.y,
    vx: npc.vx,
    vy: npc.vy,
    health: npc.health,
    maxHealth: npc.maxHealth,
    aiState: npc.aiState,
    packId: npc.packId,
    isLeader: npc.isLeader,
    hunger: npc.hunger,
    tiredness: npc.tiredness,
    fear: npc.fear,
    targetEntityId: npc.targetEntityId,
    interpolation: null,
    animationFrame: 0,
    lastFrameTime: performance.now(),
  };
  npcsMap.set(npc.networkId, npcData);
  notifyNpcsChanged();
};

export const removeNpc = ({ networkId }: { networkId: number }): void => {
  npcsMap.delete(networkId);
  notifyNpcsChanged();
};

// Fields that UI components care about (not just rendering)
const UI_RELEVANT_NPC_FIELDS = new Set(['health', 'maxHealth', 'aiState', 'hunger']);

export const updateNpc = ({
  networkId,
  updates,
}: {
  networkId: number;
  updates: Partial<NpcData>;
}): void => {
  const npc = npcsMap.get(networkId);
  if (!npc) {
    return;
  }

  // Check if position changed - set up interpolation
  const hasPositionChange =
    updates.x !== undefined &&
    updates.y !== undefined &&
    (updates.x !== npc.x || updates.y !== npc.y);

  const interpolation: NpcInterpolationData | null = hasPositionChange
    ? {
        prevX: npc.interpolation
          ? lerp({
              a: npc.interpolation.prevX,
              b: npc.interpolation.targetX,
              t: getInterpolationT({ startTime: npc.interpolation.startTime }),
            })
          : npc.x,
        prevY: npc.interpolation
          ? lerp({
              a: npc.interpolation.prevY,
              b: npc.interpolation.targetY,
              t: getInterpolationT({ startTime: npc.interpolation.startTime }),
            })
          : npc.y,
        targetX: updates.x as number,
        targetY: updates.y as number,
        startTime: performance.now(),
      }
    : npc.interpolation;

  // PERFORMANCE: Mutate map in place instead of creating new Map
  npcsMap.set(networkId, { ...npc, ...updates, interpolation });

  // Only notify if UI-relevant fields changed (skip position-only updates)
  const hasUIChange = Object.keys(updates).some((key) => UI_RELEVANT_NPC_FIELDS.has(key));
  if (hasUIChange) {
    notifyNpcsChanged();
  }
};

// Update animation frame for an animal
// PERFORMANCE: This is called frequently during animations - uses mutable update
// and does NOT notify (animation state is render-only, not UI-relevant)
export const updateNpcAnimation = ({
  networkId,
  frame,
  time,
}: {
  networkId: number;
  frame: number;
  time: number;
}): void => {
  const npc = npcsMap.get(networkId);
  if (!npc) {
    return;
  }

  // PERFORMANCE: Mutate in place, no notification needed for animation state
  npcsMap.set(networkId, {
    ...npc,
    animationFrame: frame,
    lastFrameTime: time,
  });
  // Note: No notifyNpcsChanged() - animation updates don't need to trigger UI re-renders
};

// ============================================================================
// Helpers
// ============================================================================

const lerp = ({ a, b, t }: { a: number; b: number; t: number }): number => {
  return a + (b - a) * t;
};

const smoothStep = ({ t }: { t: number }): number => {
  return t * t * (3 - 2 * t);
};

const getInterpolationT = ({ startTime }: { startTime: number }): number => {
  const elapsed = performance.now() - startTime;
  const linear = Math.min(1, elapsed / SNAPSHOT_INTERVAL_MS);
  return smoothStep({ t: linear });
};

// Preallocated result object for performance
const _interpolatedPosResult = { x: 0, y: 0 };

export const getInterpolatedNpcPosition = ({
  npc,
}: {
  npc: NpcData;
}): Readonly<{ x: number; y: number }> => {
  if (!npc.interpolation) {
    _interpolatedPosResult.x = npc.x;
    _interpolatedPosResult.y = npc.y;
    return _interpolatedPosResult;
  }

  const t = getInterpolationT({ startTime: npc.interpolation.startTime });

  _interpolatedPosResult.x = lerp({
    a: npc.interpolation.prevX,
    b: npc.interpolation.targetX,
    t,
  });
  _interpolatedPosResult.y = lerp({
    a: npc.interpolation.prevY,
    b: npc.interpolation.targetY,
    t,
  });
  return _interpolatedPosResult;
};

export const getNpc = ({ networkId }: { networkId: number }): NpcData | undefined => {
  return npcs.value.get(networkId);
};

// Click radius in tiles - allows clicking near NPCs to select them
const NPC_CLICK_RADIUS = 0.75;
const NPC_CLICK_RADIUS_SQ = NPC_CLICK_RADIUS * NPC_CLICK_RADIUS;

export const getNpcAtPosition = ({
  worldX,
  worldY,
}: {
  worldX: number;
  worldY: number;
}): NpcData | null => {
  // Convert world coordinates to tile units for distance calculation
  const clickTileX = worldX / TILE_SIZE;
  const clickTileY = worldY / TILE_SIZE;

  let closestNpc: NpcData | null = null;
  let closestDistSq = NPC_CLICK_RADIUS_SQ;

  for (const npc of npcs.value.values()) {
    // Calculate distance from click to NPC center (npc position + 0.5)
    const dx = npc.x + 0.5 - clickTileX;
    const dy = npc.y + 0.5 - clickTileY;
    const distSq = dx * dx + dy * dy;

    if (distSq < closestDistSq) {
      closestDistSq = distSq;
      closestNpc = npc;
    }
  }

  return closestNpc;
};

export const clearNpcs = (): void => {
  npcsMap.clear();
  notifyNpcsChanged();
};
